/**
 * Active Turn Screen — Card Reader sees the card, timer, and action buttons.
 * Single-device mode.
 */

import { getState, getCurrentCard, nextCard, skipCard, goBack, tickTimer } from '../game-state';
import { RoundNumber } from '../../shared/types';
import { navigate } from '../router';
import { t, getRoundName, tCard } from '../i18n';

let timerInterval: number | null = null;

export function renderTurnActive(): void {
  clearTimer();
  render();
  startTimer();
}

function startTimer(): void {
  timerInterval = window.setInterval(() => {
    tickTimer();
    const state = getState();
    if (state.phase === 'turn-end') {
      clearTimer();
      navigate('turn-end');
      return;
    }
    render();
  }, 1000);
}

function clearTimer(): void {
  if (timerInterval !== null) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
}

function render(): void {
  const app = document.getElementById('app')!;
  const state = getState();
  const card = getCurrentCard();
  const round = state.currentRound;

  // Timer styling
  let timerClass = 'timer-display';
  if (state.timerRemaining <= 10) timerClass += ' danger';
  else if (state.timerRemaining <= 20) timerClass += ' warning';

  const minutes = Math.floor(state.timerRemaining / 60);
  const seconds = state.timerRemaining % 60;
  const timerText = `${minutes}:${String(seconds).padStart(2, '0')}`;

  if (!card) {
    // Deck is empty — this shouldn't normally happen mid-render
    app.innerHTML = `
      <div class="center">
        <h2>${t('deckEmpty')}</h2>
        <p class="text-secondary">${t('deckEmptyDesc')}</p>
      </div>
    `;
    clearTimer();
    return;
  }

  const hasPrevious = state.previousCard !== null;
  const pointStars = '★'.repeat(card.points) + '☆'.repeat(4 - card.points);
  const cardPlural = state.unplayedCards.length !== 1 ? 's' : '';

  app.innerHTML = `
    <div class="stack">
      <!-- Header: Timer + Round -->
      <div class="row" style="justify-content: space-between;">
        <div class="${timerClass}">${timerText}</div>
        <span class="badge">R${round}: ${getRoundName(round)}</span>
      </div>

      <!-- Card -->
      <div class="game-card">
        <div class="card-category">${card.category}</div>
        <div class="card-title">${card.title}</div>
        <div class="card-description">${tCard(card.id, card.description)}</div>
        <div class="card-points">${pointStars} (${card.points} ${card.points > 1 ? t('pts') : t('pt')})</div>
      </div>

      <!-- Primary Action: Next (full-width, prominent) -->
      <button class="btn btn-primary btn-next-card" id="btn-next">${t('next')}</button>

      <!-- Secondary Actions: Back + Skip (smaller, separated) -->
      <div class="action-buttons-secondary">
        <button class="btn btn-secondary" id="btn-back" ${!hasPrevious ? 'disabled' : ''}>${t('back')}</button>
        <button class="btn btn-danger" id="btn-skip">${t('skip')}</button>
      </div>

      <!-- Score Bar -->
      <div class="score-bar">
        <div>
          <span class="team-label">${t('teamA')}</span>
          <span class="team-score">&nbsp;${state.scores.A}</span>
        </div>
        <div>
          <span class="team-score">${state.scores.B}&nbsp;</span>
          <span class="team-label">${t('teamB')}</span>
        </div>
      </div>

      <!-- Cards remaining -->
      <p class="center text-muted" style="font-size: var(--font-size-xs);">
        ${t('cardsRemaining', state.unplayedCards.length, cardPlural)}
      </p>
    </div>
  `;

  // Attach handlers (re-attached each render since innerHTML is replaced)
  document.getElementById('btn-next')?.addEventListener('click', () => {
    nextCard();
    const s = getState();
    if (s.phase === 'turn-end' || s.phase === 'round-complete') {
      clearTimer();
      navigate(s.phase === 'round-complete' ? 'round-complete' : 'turn-end');
    } else if (s.phase === 'mid-round-transition') {
      clearTimer();
      navigate('mid-round-transition');
    } else {
      render();
    }
  });

  document.getElementById('btn-skip')?.addEventListener('click', () => {
    skipCard();
    render();
  });

  document.getElementById('btn-back')?.addEventListener('click', () => {
    if (goBack()) render();
  });
}
